import { Service, service, inject } from './di'
import { ConfigService } from './ConfigService'
import { AnalyticsService } from './AnalyticsService'
import { SeoService } from './SeoService'
import { fetchJSON } from '../utils/fetch'
import { https } from '../utils/http'
import {
    FlyerJSON,
    ItemJSON,
    LoyaltyProgramJSON,
    StoreInfoJSON,
    DataJSON,
    SearchJSON,
    DisplayType,
    FlyerItemJSON,
    ItemDetailsJSON,
    SelectStoreJSON,
    FlyerPage,
    CorrectionNotice,
    MultiEcomItemJSON,
} from '../definitions/ApiDefinitions'
import { DateTimeService } from './DateTimeService'

@service('apiService')
export class ApiService extends Service {
    @inject config: ConfigService
    @inject seoService: SeoService

    private analytics: AnalyticsService = AnalyticsService.getInstance()

    private get _locale() {
        return this.seoService.locale
            ? this.seoService.locale.split('-').shift()
            : 'en'
    }

    getFlyersData(postalCode: string): Promise<DataJSON> {
        const url = new URL(`${this.config.API_SERVER}/data`)
        url.searchParams.append('locale', this._locale)
        url.searchParams.append('postal_code', postalCode)
        url.searchParams.append('sid', this.getSessionId())

        const guid = this.analytics.getAccountGuid()
        if (guid) {
            url.searchParams.append('guid', guid)
        }

        // Pass in extra parameter to backflipp if we're in distribution to they can server appropriate content
        // Now we are passing this extra param only for Hero Modules. Other modules should rely on Flipp Web Analytics
        if (this.config.aidHero) {
            url.searchParams.append('channel', 'dist')
        }

        return fetchJSON<DataJSON>(url.toString())
            .then((json: DataJSON) => {
                json.flyers.forEach((flyer) => {
                    flyer.thumbnail_url = https(flyer.thumbnail_url)
                    if (
                        flyer.merchant_logo &&
                        flyer.merchant_logo !== '/images/bg/logo_bg.gif'
                    )
                        flyer.merchant_logo = https(flyer.merchant_logo)
                    else delete flyer.merchant_logo
                })

                json.coupons.forEach((coupon) => {
                    coupon.coupon_image_url = https(coupon.coupon_image_url)
                    coupon.barcode_image_url = https(coupon.barcode_image_url)
                    coupon.merchant_logo_url = https(coupon.merchant_logo_url)
                    coupon.brand_logo_url = https(coupon.brand_logo_url)
                })

                json.flyer_item_coupons.forEach((item) => {
                    item.item_cutout_url = https(item.item_cutout_url)
                })

                return json
            })
            .catch(() => {
                return {
                    category_sort_csv: '',
                    coupon_category_sort_csv: '',
                    coupons: [],
                    flyer_item_coupons: [],
                    flyers: [],
                    loyalty_program_coupons: [],
                }
            })
    }

    async getFlyer(
        id: string,
        postalCode: string,
        fastFlyer: boolean = false
    ): Promise<FlyerJSON> {
        try {
            const requestsArr = [
                fetchJSON<FlyerItemJSON[]>(
                    `${this.config.API_SERVER}/flyers/${id}/flyer_items?locale=${this._locale}`
                ),
                fetchJSON<FlyerPage[]>(
                    `${this.config.API_SERVER}/flyers/${id}/flyer_pages?locale=${this._locale}`
                ),
                fetchJSON<CorrectionNotice[]>(
                    `${this.config.API_SERVER}/flyers/${id}/correction_notices?locale=${this._locale}`
                ),
                fetchJSON<SelectStoreJSON[]>(
                    `${this.config.API_SERVER}/flyers/${id}/stores/nearby?postal_code=${postalCode}&locale=${this._locale}`
                ).then((select_stores: SelectStoreJSON[]) => {
                    return {
                        info: { select_stores },
                    } as StoreInfoJSON
                }),
            ]

            if (fastFlyer) {
                requestsArr.push(
                    fetchJSON<any>(
                        `${this.config.API_SERVER}/flyers/${id}?postal_code=${postalCode}&sid=${this.getSessionId()}&locale=${this._locale}`
                    )
                )
            }
            const [
                items,
                pages,
                correction_notices,
                store_info,
                flyerDetails,
            ]: any = await Promise.all<any>(requestsArr)

            ;(items as FlyerItemJSON[]).forEach((item: any) => {
                item.cutout_image_url = https(item.cutout_image_url)
            })

            const flyer = (flyerDetails ? { ...flyerDetails } : null) as any

            if (fastFlyer && flyer) {
                flyer.thumbnail_url = https(flyer.thumbnail_url)

                if (
                    flyer.merchant_logo &&
                    flyer.merchant_logo !== '/images/bg/logo_bg.gif'
                ) {
                    flyer.merchant_logo = https(flyer.merchant_logo)
                } else {
                    delete flyer.merchant_logo
                }
            }

            return {
                items,
                pages,
                correction_notices,
                store_info,
                flyer,
            }
        } catch (e) {
            return null
        }
    }

    getFlyerItem(id: string, postalCode?: any): Promise<ItemJSON> {
        const prefix = postalCode ? `&postal_code=${postalCode}` : ''
        const distType = this.getDistTypeAppendix()
        const requestUrl = `${this.config.API_SERVER}/flyer_items/${id}?locale=${this._locale}${distType}&sid=${this.getSessionId()}${prefix}`
        const dtService = DateTimeService.getInstance()
        return fetchJSON<ItemDetailsJSON>(requestUrl)
            .then((json: ItemDetailsJSON) => {
                if (!json || (<any>json).error) return null

                json.merchant_logo = https(json.merchant_logo)
                json.cutout_image_url = https(json.cutout_image_url)
                json.image_url = https(json.image_url)
                json.ttm_url =
                    json.ttm_url && json.display_type === DisplayType.IFRAME
                        ? https(json.ttm_url)
                        : json.ttm_url
                json.valid_to = json.valid_to
                    ? dtService.convertTimeToZulu(json.valid_to, true)
                    : dtService.convertTimeToZulu(json.flyer_valid_to, true)
                json.valid_from = json.valid_from
                    ? dtService.convertTimeToZulu(json.valid_from)
                    : dtService.convertTimeToZulu(json.flyer_valid_from)

                return { item: json }
            })
            .catch(() => {
                return null
            })
    }

    search(postalCode: string, query: string): Promise<SearchJSON> {
        return fetchJSON<SearchJSON>(
            `${this.config.API_SERVER}/flyer_items/search?locale=${this._locale}&postal_code=${postalCode}&q=${encodeURIComponent(query)}`
        ).then((json: SearchJSON) => {
            json.items.forEach((item) => {
                if (
                    item.merchant_image_url &&
                    item.merchant_image_url !== '/images/bg/logo_bg.gif'
                )
                    item.merchant_image_url = https(item.merchant_image_url)
                else delete item.merchant_image_url
                item.clipping_image_url = https(item.clipping_image_url)
            })

            if (Array.isArray(json.ecom_items) && json.ecom_items.length > 0) {
                json.ecom_items.forEach((ecomItemObj) => {
                    ecomItemObj.merchant_logo = https(ecomItemObj.merchant_logo)
                    ecomItemObj.image_url = https(ecomItemObj.image_url)
                })
            }

            return json
        })
    }

    getLoyaltyPrograms(postalCode: string) {
        return fetchJSON<LoyaltyProgramJSON>(
            `${this.config.API_SERVER}/loyalty_programs?postal_code=${postalCode}&locale=${this._locale}`
        )
            .then((cards: LoyaltyProgramJSON) => {
                /* Filter our loyalty programs if 'validation_method' is not set (ie rebate programs) See FW-986.
                 * Rationale: It’s not as simple as `!is_rebate && is_load_to_card`.  The problem is that there
                 * are still “wallet-only” cards and those won’t render.  Ideally there is an `is_wallet` bool
                 * that represents whether a card logically makes sense as a card, but in lieu of that, we
                 * decided to use `validation_method != null` as a proxy for that bool because it effectively
                 * means the same thing.
                 * */
                cards.loyalty_programs = cards.loyalty_programs.filter(
                    (card) => card.validation_method
                )
                return cards
            })
            .catch(() => {
                return {
                    loyalty_programs: [],
                    loyalty_program_merchants: [],
                } as LoyaltyProgramJSON
            })
    }

    getMultiEcomItems(globalIds: string[]): Promise<MultiEcomItemJSON> {
        return fetchJSON(
            `${this.config.ECOM_ITEMS_SERVER}?global_ids=${globalIds.join(',')}`
        ).then((json: MultiEcomItemJSON) => {
            if (!json || (<any>json).error) {
                return null
            }

            return json
        })
    }

    private getSessionId() {
        return this.analytics.sessionId
    }

    private getDistTypeAppendix() {
        return this.config.aidHero ? '&channel=dist' : ''
    }
}
